import { ElementProps } from '@/components/sections/builder/types';
import { ProductDetailVariantPickerElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { Shirt } from 'lucide-react';
import { shallow } from 'zustand/vanilla/shallow';
import { IProduct, ProductAssets, ProductItem } from '@/types/product';
import React, { useMemo, useState } from 'react';

import { ThemeType, useBuilderStore } from '@/stores/builder';
import { useProductStore } from '@/stores/product';
import { useCartProduct } from '@/stores/cart';
import { cn } from '@/lib/utils';
import { PillsPicker } from '@/components/sections/builder/elements/body/product-detail/variant-picker/components/pills-picker';
import { DropdownPicker } from '@/components/sections/builder/elements/body/product-detail/variant-picker/components/dropdown-picker';

export type VariantType = {
  label: string;
  variant_id: string;
  options: { value: string; label: string; option_id: string }[];
};

const defaultData: ProductDetailVariantPickerElementDataType = {
  name: 'Variant Picker',
  style: 'pills',
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

  const themeBuilder = useBuilderStore((state) => state.theme, shallow);
  const cartProduct = useCartProduct((state) => state, shallow);
  const productStore = useProductStore((state) => state, shallow);

  const parentData = useMemo(() => (element.parentData || {}) as IProduct, [element.parentData]);

  const variants = useMemo(
    () => (parentData.variants || []) as unknown as VariantType[],
    [parentData]
  );
  const items = useMemo(
    () => (parentData.product_items || []) as unknown as ProductItem[],
    [parentData]
  );

  const theme = preview ? themeBuilder : ((element.theme || {}) as ThemeType);

  const handleSelectVariant = (variantId: string, optionId: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [variantId]: prev[variantId] === optionId ? '' : optionId,
    }));
  };

  const allSelectedVariants = React.useMemo(() => {
    return variants.every((variant) => selectedOptions[variant.variant_id]);
  }, [variants, selectedOptions]);

  const getStockBySelectedOptions = (): ProductItem | undefined => {
    const selectedCombination = Object.values(selectedOptions).filter(Boolean).join(':');
    const matchingItem = items.find((item) => item.item_option_id === selectedCombination);
    return matchingItem;
  };

  const productItem = getStockBySelectedOptions();

  React.useEffect(() => {
    const emptySelectedOptions = Object.fromEntries(variants.map((v) => [v.variant_id, '']));
    setSelectedOptions(emptySelectedOptions);
  }, [theme.variantPill?.style]);

  React.useEffect(() => {
    const hasDashOption = Object.values(selectedOptions).includes('');

    if (hasDashOption) {
      productStore.setImageVariant([]);
      productStore.setQuantity(1);
      return;
    }

    if (!productItem) return;

    productStore.setQuantity(productItem.stock || 0);
    productStore.setPrice(productItem.sell_price || 0);
    productStore.setDiscountPrice(productItem.discount_price || 0);
    productStore.setImageVariant((productItem.images as ProductAssets[]) || []);

    cartProduct.setVariant({
      ...productItem,
      variants: productItem.variants,
    });
  }, [productItem]);

  React.useEffect(() => {
    productStore.setAllSelectedVariants(!!allSelectedVariants);
  }, [allSelectedVariants]);

  return (
    <div className={cn('flex w-full max-w-full', variants.length > 0 && 'border-t pt-4')}>
      {theme.variantPill?.style === 'pills' ? (
        <PillsPicker
          product={parentData}
          variants={variants || []}
          items={items || []}
          selectedOptions={selectedOptions}
          handleSelect={handleSelectVariant}
        />
      ) : (
        <div className='w-full'>
          <DropdownPicker
            product={parentData}
            variants={variants || []}
            items={items || []}
            selectedOptions={selectedOptions}
            handleSelect={handleSelectVariant}
          />
        </div>
      )}
    </div>
  );
};

export const ElementPreviewIcon = Shirt;

export function ElementSetting() {
  return (
    <div>
      <span className='mt-2 mb-4 font-bold'>
        Settings are not available, for settings it is in the global theme
      </span>
    </div>
  );
}

const ProductDetailVariantPicker = {
  type: 'variant_picker',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailVariantPicker;
